import { Navigate, useLocation, type RouteObject } from "react-router";

import { RoleViewProvider } from "./shell/roleView";
import { Shell } from "./shell/Shell";
import { AgentLandingPage } from "./features/auth/AgentLandingPage";
import { BrokeragePage } from "./features/auth/BrokeragePage";
import { Callback } from "./features/auth/Callback";
import { JoinPage } from "./features/auth/JoinPage";
import { AddDevice } from "./features/auth/AddDevice";
import { Login } from "./features/auth/Login";
import { CompleteSignup } from "./features/auth/CompleteSignup";
import { SignIn } from "./features/auth/SignIn";
import { Smoke } from "./features/auth/Smoke";
import { Home } from "./features/home/Home";
import { DTIWorksheet } from "./features/mortgage/DTIWorksheet";
import { LoanApplication } from "./features/mortgage/LoanApplication";
import { MyDossier } from "./features/documents/MyDossier";
// PropertySearch is hidden for the initial deploy (Phase-0 mock, no property-svc
// backend). PropertyDetail was removed by spec 024: listing search is not
// built, so the one listing it showed was invented.
import { VoiceCall } from "./features/voice/VoiceCall";
import { SignDocument } from "./features/trec/SignDocument";
import { MyAgreements } from "./features/trec/MyAgreements";
import { RequestShowing } from "./features/appointments/RequestShowing";
import { ClientProfile } from "./features/profile/ClientProfile";
import { AuthLayout } from "./shell/AuthLayout";
import { NotFound } from "./shell/NotFound";

// Sign-up lived at /sign-in, which read as the sign-in page next to /login.
// Old links and bookmarks still arrive there, so forward them — keeping the
// router state the brokerage and agent pages hand over (the tenant to join),
// and any query string.
function LegacySignUpRedirect() {
  const location = useLocation();
  return (
    <Navigate
      to={{ pathname: "/sign-up", search: location.search }}
      state={location.state}
      replace
    />
  );
}

export const routes: RouteObject[] = [
  // Auth screens live OUTSIDE the Shell — no role-aware nav for users who
  // haven't signed in yet. /sign-up = new account, /login = returning user.
  /* { path: "/sign-in", element: <SignIn /> },
  // Step TWO of signup: the inbox has been proven, now make the passkey. The account is
  // created HERE, not on /sign-in — that ordering is the account-takeover fix (V057).
  { path: "/verify-email", element: <CompleteSignup /> },
  { path: "/login", element: <Login /> },
  // Registering a passkey on a SECOND device (V072). Anonymous by design: the
  // token in the URL is the authorisation, and the point is that the person
  // opening it cannot yet sign in on this device.
  { path: "/add-device", element: <AddDevice /> },
  { path: "/join", element: <JoinPage /> },
  { path: "/b/:slug", element: <BrokeragePage /> },
  { path: "/b/:slug/a/:agentId", element: <AgentLandingPage /> },
  { path: "/auth/callback", element: <Callback /> },
  { path: "/auth/smoke", element: <Smoke /> },
  */

  {
    element: <AuthLayout />,
    children: [
      { path: "/sign-up", element: <SignIn /> },
      { path: "/sign-in", element: <LegacySignUpRedirect /> },
      { path: "/verify-email", element: <CompleteSignup /> },
      { path: "/login", element: <Login /> },
      { path: "/add-device", element: <AddDevice /> },
      { path: "/join", element: <JoinPage /> },
      { path: "/b/:slug", element: <BrokeragePage /> },
      { path: "/b/:slug/a/:agentId", element: <AgentLandingPage /> },
      { path: "/auth/callback", element: <Callback /> },
      { path: "/auth/smoke", element: <Smoke /> },
    ],
  },

  {
    // RoleViewProvider wraps the layout so it remounts (and re-reads the saved
    // session) when the user returns from the auth screens after signing in.
    element: (
      <RoleViewProvider>
        <Shell />
      </RoleViewProvider>
    ),
    // A route that throws renders this INSIDE the shell, so the client keeps
    // the nav and can get somewhere else without using the back button.
    errorElement: <NotFound />,
    children: [
      { path: "/", element: <Home /> },
      { path: "/voice/call/:callId", element: <VoiceCall /> },
      { path: "/trec/sign/:formId", element: <SignDocument /> },
      { path: "/agreements", element: <MyAgreements /> },
      { path: "/appointments/request", element: <RequestShowing /> },
      { path: "/mortgage/:applicationId/dti", element: <DTIWorksheet /> },
      {
        path: "/mortgage/:applicationId/application",
        element: <LoanApplication />,
      },
      // Id-less: the borrower's own, resolved (and opened) server-side. This is
      // the link their checklist points at — they have no application id to know.
      { path: "/loan-application", element: <LoanApplication /> },
      { path: "/dossier/:dossierId", element: <MyDossier /> },
      { path: "/profile", element: <ClientProfile /> },
    ],
  },
  // LAST, and outside the shell: a hand-typed or stale URL belongs to nobody's
  // layout, and the visitor may not be signed in. React Router matches in
  // order, so this only sees paths nothing above claimed.
  { path: "*", element: <NotFound /> },
];
